import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  attractionCreateInputSchema,
  type Attraction,
  type AttractionCreateInput,
  type AvatarFit,
  type FaceFit,
} from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { FloatingField } from '@/components/ui/floating-field';
import { FaceFitEditor } from '@/components/attractions/face-fit-editor';
import { AvatarCropEditor } from '@/components/users/avatar-crop-editor';
import {
  useCreateAttraction,
  useUpdateAttraction,
  useUploadAttractionPhotoOriginal,
  useUploadAttractionPortrait,
  useSaveAttractionFaceFit,
  useSaveAttractionPhotoFit,
} from '@/hooks/use-attractions';
import { useAuthStore } from '@/stores/auth-store';

interface AttractionFormModalProps {
  open: boolean;
  attraction?: Attraction | null;
  onClose: () => void;
}

interface PendingPortrait {
  url: string;
  imgW: number;
  imgH: number;
}

export function AttractionFormModal({ open, attraction, onClose }: AttractionFormModalProps) {
  const isEdit = !!attraction;
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const isManager = isAdmin || role === 'gerente' || role === 'gestor';

  const createMut = useCreateAttraction();
  const updateMut = useUpdateAttraction();
  const uploadPhoto = useUploadAttractionPhotoOriginal();
  const uploadPortrait = useUploadAttractionPortrait();
  const saveFaceFit = useSaveAttractionFaceFit();
  const savePhotoFit = useSaveAttractionPhotoFit();

  const [pendingPortrait, setPendingPortrait] = useState<PendingPortrait | null>(null);
  const [editorInitialFit, setEditorInitialFit] = useState<FaceFit | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const [photoEditorUrl, setPhotoEditorUrl] = useState<string | null>(null);
  const [photoEditorW, setPhotoEditorW] = useState<number | null>(null);
  const [photoEditorH, setPhotoEditorH] = useState<number | null>(null);

  const form = useForm<AttractionCreateInput>({
    resolver: zodResolver(attractionCreateInputSchema),
    defaultValues: { brand_id: 1, name: '', instagram: '' },
  });

  useEffect(() => {
    if (!open) return;
    if (attraction) {
      form.reset({
        brand_id: attraction.brand_id,
        name: attraction.name,
        instagram: attraction.instagram ?? '',
      });
    } else {
      form.reset({ brand_id: 1, name: '', instagram: '' });
    }
  }, [open, attraction, form]);

  function originalUrl(url: string) {
    return url.replace('.webp', '-original.webp');
  }

  function loadImgDimensions(url: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Falha ao carregar imagem'));
      img.src = url;
    });
  }

  async function onSubmit(data: AttractionCreateInput) {
    try {
      if (isEdit && attraction) {
        await updateMut.mutateAsync({
          id: attraction.id,
          input: { name: data.name, instagram: data.instagram || null },
        });
        toast.success('DJ atualizado');
      } else {
        await createMut.mutateAsync({
          brand_id: data.brand_id,
          name: data.name,
          instagram: data.instagram || null,
        });
        toast.success('DJ cadastrado');
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      toast.error(msg);
    }
  }

  async function handleProfilePhotoPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !attraction) return;
    try {
      const result = await uploadPhoto.mutateAsync({ id: attraction.id, file });
      const origUrl = originalUrl(result.photo_url);
      setPhotoEditorUrl(origUrl);
      setPhotoEditorW(result.img_w);
      setPhotoEditorH(result.img_h);
      setPhotoEditorOpen(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar foto';
      toast.error(msg);
    }
  }

  async function handleAdjustProfilePhoto() {
    if (!attraction?.photo_url) return;
    try {
      const origUrl = originalUrl(attraction.photo_url);
      const { width, height } = await loadImgDimensions(origUrl);
      setPhotoEditorUrl(origUrl);
      setPhotoEditorW(width);
      setPhotoEditorH(height);
      setPhotoEditorOpen(true);
    } catch {
      toast.error('Faça upload da foto novamente para poder reposicionar');
    }
  }

  async function handlePhotoFitConfirm(fit: AvatarFit) {
    if (!attraction) return;
    try {
      await savePhotoFit.mutateAsync({ id: attraction.id, fit });
      toast.success('Foto reposicionada');
      setPhotoEditorOpen(false);
      setPhotoEditorUrl(null);
      setPhotoEditorW(null);
      setPhotoEditorH(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao reposicionar';
      toast.error(msg);
    }
  }

  async function handleCardPhotoPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !attraction) return;
    try {
      const result = await uploadPortrait.mutateAsync({ id: attraction.id, file });
      setPendingPortrait({ url: result.portrait_url, imgW: result.img_w, imgH: result.img_h });
      setEditorInitialFit(null);
      setEditorOpen(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar foto';
      toast.error(msg);
    }
  }

  async function handleAdjustExistingPortrait() {
    if (!attraction?.portrait_url) return;
    let imgW = attraction.face_fit?.img_w ?? 0;
    let imgH = attraction.face_fit?.img_h ?? 0;
    // Sem face_fit salvo não temos as dimensões — lê da imagem real em vez de
    // chutar 1080x1920 (que distorce retratos de outra proporção).
    if (!imgW || !imgH) {
      try {
        const dims = await loadImgDimensions(attraction.portrait_url);
        imgW = dims.width;
        imgH = dims.height;
      } catch {
        toast.error('Não foi possível carregar o card. Envie a foto novamente.');
        return;
      }
    }
    setPendingPortrait({ url: attraction.portrait_url, imgW, imgH });
    setEditorInitialFit(attraction.face_fit ?? null);
    setEditorOpen(true);
  }

  async function handleFaceFitConfirm(faceFit: FaceFit) {
    if (!attraction) return;
    try {
      await saveFaceFit.mutateAsync({ id: attraction.id, faceFit });
      toast.success('Encaixe salvo');
      setEditorOpen(false);
      setPendingPortrait(null);
      setEditorInitialFit(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar encaixe';
      toast.error(msg);
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title={isEdit ? 'Editar DJ' : 'Novo DJ'}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {isEdit && attraction && (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 shrink-0 rounded-full overflow-hidden border border-border bg-muted grid place-items-center text-muted-foreground">
                {attraction.photo_url ? (
                  <img
                    src={attraction.photo_url}
                    alt={attraction.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="mono text-[10px] uppercase">sem foto</span>
                )}
              </div>
              {isManager && (
                <div className="flex flex-wrap gap-2">
                  <label className="cursor-pointer rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-muted">
                    {uploadPhoto.isPending ? 'Enviando...' : 'Foto de perfil'}
                    <input
                      type="file"
                      accept="image/png,image/webp,image/jpeg"
                      className="hidden"
                      onChange={handleProfilePhotoPick}
                      disabled={uploadPhoto.isPending}
                    />
                  </label>
                  {attraction.photo_url && (
                    <button
                      type="button"
                      onClick={handleAdjustProfilePhoto}
                      className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-muted"
                    >
                      Reposicionar
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="flex items-center gap-4">
                <div className="h-16 w-11 shrink-0 rounded-lg overflow-hidden border border-border bg-muted grid place-items-center text-muted-foreground">
                  {attraction.portrait_url ? (
                    <img
                      src={attraction.portrait_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="mono text-[9px] uppercase text-center leading-tight">sem card</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className="cursor-pointer rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-muted">
                    {uploadPortrait.isPending ? 'Enviando...' : 'Foto de card'}
                    <input
                      type="file"
                      accept="image/png,image/webp,image/jpeg"
                      className="hidden"
                      onChange={handleCardPhotoPick}
                      disabled={uploadPortrait.isPending}
                    />
                  </label>
                  {attraction.portrait_url && (
                    <button
                      type="button"
                      onClick={handleAdjustExistingPortrait}
                      className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-muted"
                    >
                      Reposicionar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <FloatingField
          label="Nome do DJ"
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />
        <FloatingField
          label="Instagram (@usuario)"
          {...form.register('instagram')}
        />

        {!isEdit && (
          <p className="text-xs text-muted-foreground">
            As fotos podem ser adicionadas depois de criar o DJ.
          </p>
        )}

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={createMut.isPending || updateMut.isPending}
            className="flex-[2] rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-red disabled:opacity-60"
          >
            {createMut.isPending || updateMut.isPending
              ? 'Salvando...'
              : isEdit
                ? 'Salvar'
                : 'Criar DJ'}
          </button>
        </div>
      </form>

      <AvatarCropEditor
        open={photoEditorOpen}
        imageUrl={photoEditorUrl}
        imgW={photoEditorW}
        imgH={photoEditorH}
        isSaving={savePhotoFit.isPending}
        onClose={() => {
          setPhotoEditorOpen(false);
          setPhotoEditorUrl(null);
          setPhotoEditorW(null);
          setPhotoEditorH(null);
        }}
        onConfirm={handlePhotoFitConfirm}
      />

      <FaceFitEditor
        open={editorOpen}
        portraitUrl={pendingPortrait?.url ?? null}
        imgW={pendingPortrait?.imgW ?? null}
        imgH={pendingPortrait?.imgH ?? null}
        initialFaceFit={editorInitialFit}
        isSaving={saveFaceFit.isPending}
        onClose={() => {
          setEditorOpen(false);
          setPendingPortrait(null);
          setEditorInitialFit(null);
        }}
        onConfirm={handleFaceFitConfirm}
      />
    </BottomSheet>
  );
}
